/**
 * Platform-admin email domain allowlist, keyed on the deployment ENVIRONMENT.
 *
 * One source of truth shared by the backend add-path (`PlatformAdminService.add`)
 * and the env-aware seeder (`007-platformAdmin.seed.ts`). Kept pure/synchronous
 * (no NestJS, no `process.env` reads inside) so both a DI-managed Nest service and
 * a standalone `DataSource` seed script can import it directly.
 *
 * Rule: `local`, `sandbox`, `develop`, `dev-2`, and `preview` accept the gafetest
 * test domain in addition to cdw.com and amplifiedit.cdw.com; every other
 * environment (`testing`, `staging`, `prod`, and anything unrecognized) accepts
 * cdw.com and amplifiedit.cdw.com ONLY — testing/staging intentionally fail
 * closed (decision 2026-07-23, EDUS-2718).
 */

import { AMPLIFIEDIT_DOMAIN } from './brand-links';

/** The real CDW employee domain — allowed in every environment. */
export const ADMIN_DOMAIN_CDW = 'cdw.com';

/** The real CDW / AmplifiedIT employee domain — allowed in every environment. */
export const ADMIN_DOMAIN_AMPLIFIEDIT = AMPLIFIEDIT_DOMAIN;

/** The test domain — only acceptable in the dev-tier envs listed below. */
export const ADMIN_DOMAIN_GAFETEST = 'gafetest.com';

/**
 * Environments where the gafetest test domain is also acceptable.
 *
 * This allowlists the SMALL, permissive set on purpose: any unknown / new /
 * misconfigured ENVIRONMENT value falls through to the CDW domains only, i.e. it
 * fails CLOSED rather than accidentally granting the test domain.
 */
const GAFETEST_ALLOWED_ENVS = new Set([
  'local',
  'sandbox',
  'develop',
  'dev-2',
  'preview',
]);

/**
 * Allowed platform-admin email domains for the given ENVIRONMENT.
 *
 * @param env the ENVIRONMENT value (`'local' | 'sandbox' | 'develop' | 'dev-2' | 'preview' | 'testing' | 'staging' | 'prod'`).
 *   `undefined` / unrecognized values fail closed to cdw.com/amplifiedit.cdw.com only.
 */
export function getAllowedAdminDomains(env: string | undefined): string[] {
  return env && GAFETEST_ALLOWED_ENVS.has(env)
    ? [ADMIN_DOMAIN_CDW, ADMIN_DOMAIN_AMPLIFIEDIT, ADMIN_DOMAIN_GAFETEST]
    : [ADMIN_DOMAIN_CDW, ADMIN_DOMAIN_AMPLIFIEDIT];
}

/**
 * True if `email`'s domain is allowed for platform-admin access in `env`.
 *
 * Case-insensitive on the domain; returns `false` for a malformed email
 * (no `@`).
 */
export function isAllowedAdminEmail(email: string, env: string | undefined): boolean {
  const at = email.lastIndexOf('@');
  if (at < 0) {
    return false;
  }
  const domain = email.slice(at + 1).toLowerCase();
  return getAllowedAdminDomains(env).includes(domain);
}
